import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import debounce from 'lodash/debounce';

import Button from '../shared/button.component';
import { Tabs, Tab } from '../shared/tabs.component';
import FormHelpers from '../shared/forms/formHelpers';
import FormControl from '../shared/forms/formControl.component';
import Input from '../shared/forms/input/input.component';
import Spinner from '../shared/spinner.component';

import ConfirmationDialogContent from './confirmationDialogContent.component';
import WarningDialog from '../shared/warningDialog.component';

import {
  fetchInitialDataForUserSettingsForm,
  fetchLoggedInUserSettings,
  hideUserSettingsFormForAdd,
  hideUserSettingsFormForEdit,
  saveUserSettings,
  disableReadOnlyMode,
  checkIfUserEmailExists,
  checkIfUserEmailIsUnderDomain,
  checkIfUserSSOExists,
  showSetLastClientIdModal,
  fetchRoleAssignments,
} from './store/actions';
import { userClientPropTypes } from '../shared/propTypes/userClients';
import {
  userToEditPropTypes,
  userSettingsFormDataPropTypes,
  rolesOptionsPropTypes,
  availablePermissionsPropTypes,
  loggedInUserAssignmentsPropTypes,
  canEditClientsPropTypes,
  clientsFormDataPropTypes,
} from './propTypes';
import CompanyAssignments, { PillTypes } from './companyAssignments/companyAssignments.component';
import UserClientsGrid from './userClientsGrid/userClientsGrid.component';
import {
  rolesOptionsSelector,
  settingsFormDataSelector,
  availablePermissionsForRoleSelector,
  isAllowedToEditUsersSelector,
  normalizedLoggedInUserAssignmentsSelector,
  filteredSettingsFormDataSelector,
  canEditClientsSelector,
  currentUserClientSelector,
  clientsFormDataSelector,
  isEmailUniqueSelector,
  isUserEmailUnderDomainSelector,
  isSSOUniqueSelector,
  isCheckingIfUsersEmailExistsSelector,
  isCheckingIfUserEmailIsUnderDomainSelector,
  isCheckingIfUsersSSOExistsSelector,
  canHaveLastClientSetSelector,
  rolesAssignmentsMapsSelector,
  isLoadingRolesAssignmentsMapSelector,
  isFetchingInitialDataForUserSettingsFormSelector,
} from './store/selectors';
import getInitialState from './addEditUserSettingsForm.initialState';
import { userAssignment } from './constants';
import UserPrivilegesTab from './userPrivilegesTab.component';
import {
  filterByPrivileges,
  isAllowedToEditPrivilegesAndPermissions,
  applyNewRoleAssignments,
} from './addEditUserSettingsForm.helpers';
import { userRoles } from '../shared/authorization';
import { SSO_ALPHANUMERIC_REGEX, SSO_ALPHANUMERIC_ERROR } from '@common/constants/sharedConstants';

import { filterAssignmentsByType } from '../utils/permissionsHelpers';
import SetLastClientModal from './setLastClientModal.component';
import {
  canUserEditRole,
  getAssignmentKey,
  isInternalEmailAddress,
  setDefaultExpirationDate,
} from './utils';
import moment from 'moment';

const DUPLICATED_EMAIL_ERROR = 'User with same email already exists';
const DUPLICATED_SSO_ERROR = 'User with same sso already exists';
const USER_EMAIL_DOMAIN_ERROR = 'User email domain is not allowed';

const isSsoAlphanumeric = sso => SSO_ALPHANUMERIC_REGEX.test(String(sso ?? ''));

const TabTypes = {
  SETTINGS: 'SETTINGS',
  USER_PRIVILEGES: 'USER_PRIVILEGES',
  COMPANY_ASSIGNMENTS_AND_PRIVILEGES: 'COMPANY_ASSIGNMENTS_AND_PRIVILEGES',
};

const DEFAULT_CLIENT = {
  roleId: '',
  roleName: '',
  expirationDate: '',
  expirationSsoName: '',
};


class AddEditUserSettingsForm extends PureComponent {
  static propTypes = {
    fetchInitialDataForUserSettingsForm: PropTypes.func.isRequired,
    fetchLoggedInUserSettings: PropTypes.func.isRequired,
    hideUserSettingsFormForAdd: PropTypes.func.isRequired,
    hideUserSettingsFormForEdit: PropTypes.func.isRequired,
    saveUserSettings: PropTypes.func.isRequired,
    openForEdit: PropTypes.bool,
    userToEditSso: PropTypes.string,
    userToEdit: userToEditPropTypes,
    loggedInUser: userToEditPropTypes,
    readOnly: PropTypes.bool.isRequired,
    isSavingInProgress: PropTypes.bool.isRequired,
    currentPeriod: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    userSettingsFormData: userSettingsFormDataPropTypes.isRequired,
    deselectUser: PropTypes.func.isRequired,
    disableReadOnlyMode: PropTypes.func.isRequired,
    userRole: PropTypes.string.isRequired,
    setUserModified: PropTypes.func.isRequired,
    isUserModified: PropTypes.bool.isRequired,
    showUserSettingsFormForView: PropTypes.func.isRequired,
    rolesOptions: rolesOptionsPropTypes.isRequired,
    doesNotHavePermissionToEdit: PropTypes.bool,
    availablePermissions: availablePermissionsPropTypes.isRequired,
    loggedInUserSso: PropTypes.string.isRequired,
    loggedInUserAssignments: loggedInUserAssignmentsPropTypes,
    canEditClients: canEditClientsPropTypes.isRequired,
    defaultClient: userClientPropTypes,
    clientsFormData: clientsFormDataPropTypes.isRequired,
    canModify: PropTypes.bool.isRequired,
    checkIfUserSSOExists: PropTypes.func.isRequired,
    checkIfUserEmailExists: PropTypes.func.isRequired,
    checkIfUserEmailIsUnderDomain: PropTypes.func.isRequired,
    isCheckingIfUsersSSOExists: PropTypes.bool.isRequired,
    isCheckingIfUsersEmailExists: PropTypes.bool.isRequired,
    isCheckingIfUserEmailIsUnderDomain: PropTypes.bool.isRequired,
    isSSOUnique: PropTypes.bool.isRequired,
    isEmailUnique: PropTypes.bool.isRequired,
    isUserEmailUnderDomain: PropTypes.bool.isRequired,
    showSetLastClientIdModal: PropTypes.func.isRequired,
    canHaveLastClientSet: PropTypes.bool.isRequired,
    rolesAssignmentsMap: PropTypes.shape({}),
    fetchRoleAssignments: PropTypes.func.isRequired,
    isLoadingRolesAssignmentsMap: PropTypes.bool,
    usersGridApi: PropTypes.shape({}).isRequired,
    isFetchingInitialDataForUserSettingsForm: PropTypes.bool,
    canEditExpirationDate: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...getInitialState(),
      isEditMode: props.openForEdit,
      activeTab: TabTypes.SETTINGS,
      activeCompanyAssignmentsPill: PillTypes.CURRENT_ASSIGNMENTS,
      initialCheckedAssignments: [],
    };
  }

  componentDidMount() {
    const {
      loggedInUserSso,
      userToEditSso,
      fetchInitialDataForUserSettingsForm,
      fetchLoggedInUserSettings,
      openForEdit,
      currentPeriod,
      loggedInUser,
      defaultClient,
    } = this.props;

    // As selecting other client is not possible, please remove all this.state.selectedClient occurrences and replace them by defaultClient
    this.setState({ selectedClient: defaultClient });

    fetchInitialDataForUserSettingsForm({
      userToEditSso,
      openForEdit,
      currentPeriod,
    });

    if (!loggedInUser) {
      fetchLoggedInUserSettings({ sso: loggedInUserSso });
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.userToEdit === null && nextProps.userToEdit) {
      this.setDataForEdit(nextProps.userToEdit, this.props.loggedInUser);
    }
    if (this.props.loggedInUser === null && nextProps.loggedInUser) {
      this.setDataForEdit(this.props.userToEdit, nextProps.loggedInUser);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      userToEditSso,
      fetchInitialDataForUserSettingsForm,
      openForEdit,
      currentPeriod,
      readOnly,
      userToEdit,
    } = this.props;

    const { formData } = this.state;

    if ((formData.checkedClients.value !== prevState.formData.checkedClients.value)
      || (formData.role.value !== prevState.formData.role.value)) {
      this.customRoleValidation(formData);
    }

    if ((formData.checkedClients.value !== prevState.formData.checkedClients.value)
      || (formData.expirationDate.value !== prevState.formData.expirationDate.value)) {
      this.customExpirationDateValidation(formData);
    }

    if (!openForEdit) {
      if (!!formData.email.value && (formData.email.value !== prevState.formData.email.value)) {
        this.checkIfUserEmailExists({
          email: formData.email.value,
        });
        this.checkIfUserEmailIsUnderDomain({
          email: formData.email.value,
        });
      }
      if (formData.sso.value !== prevState.formData.sso.value) {
        this.checkIfUserSSOExists({
          sso: formData.sso.value,
        });
      }
    }

    if (openForEdit && userToEdit) {
      if (
        userToEdit.email !== formData.email.value
        && formData.email.value !== prevState.formData.email.value
      ) {
        this.checkIfUserEmailExists({
          email: formData.email.value,
        });
        this.checkIfUserEmailIsUnderDomain({
          email: formData.email.value,
        });
      }
    }

    if (!userToEditSso) {
      return;
    }

    if (readOnly === prevProps.readOnly && userToEditSso === prevProps.userToEditSso) {
      return;
    }

    if (readOnly !== prevProps.readOnly && this.userClientsGridApi) {
      this.userClientsGridApi.redrawRows();
    }

    fetchInitialDataForUserSettingsForm({
      userToEditSso,
      openForEdit,
      currentPeriod,
    });
  }

  checkIfUserSSOExists = debounce(data => {
    const { checkIfUserSSOExists } = this.props;
    checkIfUserSSOExists(data);
  }, 500);

  checkIfUserEmailExists = debounce(data => {
    const { checkIfUserEmailExists } = this.props;
    checkIfUserEmailExists(data);
  }, 500);

  checkIfUserEmailIsUnderDomain = debounce(data => {
    const { checkIfUserEmailIsUnderDomain } = this.props;
    checkIfUserEmailIsUnderDomain(data);
  }, 500);

  onUserClientsGridReady = params => {
    this.userClientsGridApi = params.api;
  };

  setDataForEdit = (userToEdit, loggedInUser) => {
    const { userRole, defaultClient } = this.props;
    const isSuperUser = userRole === userRoles.SUPER_USER;

    let initialCheckedAssignments = [];
    if (userToEdit && userToEdit.clients) {
      const currentClient = userToEdit.clients.find(client => client.clientId === defaultClient.clientId);
      if (currentClient && currentClient.assignments) {
        initialCheckedAssignments = currentClient.assignments
          .filter(assignment => assignment.assignmentType !== 'USR')
          .map(assignment => {
            const updatedPrivileges = assignment.privileges.map(privilege => privilege.privilegeCode);
            return { ...assignment, clientId: currentClient.clientId, privileges: updatedPrivileges };
          });
      }
    }

    this.setState(({ elementsToValidate }) => ({
      ...getInitialState(userToEdit, loggedInUser, isSuperUser),
      elementsToValidate,
      initialCheckedAssignments,
    }));
  };

  validate = (key, { isValid }) => {
    FormHelpers.validate({
      key,
      isValid,
      setState: this.setState.bind(this),
      callback: () => {
        this.setState({
          shouldResetValues: false,
        }, () => {
          this.validateAll();
        });
      },
    });
  };

  validationSubscription = (key, operation) => {
    FormHelpers.validationSubscription({
      key,
      operation,
      setState: this.setState.bind(this),
      callback: this.validateAll,
    });
  };

  validateAll = () => {
    FormHelpers.validateAll(this.setState.bind(this));
  };

  customRoleValidation = formData => {
    if (!formData.checkedClients.value.length) {
      this.setState({
        roleErrorMessage: 'Role is required.',
        isRoleValid: false,
      }, () => this.validateAll());
      return;
    }

    const clientsWithoutRoleValues = formData.checkedClients.value.filter(checkedClient => (
      !formData.role.value.some(role => role.clientId === checkedClient.clientId)
    ));

    if (clientsWithoutRoleValues.length) {
      const clientNames = clientsWithoutRoleValues.map(({ name }) => `"${name}"`).join(', ');
      this.setState({
        roleErrorMessage: `Role is required. Clients ${clientNames} do not have a role assignment.`,
        isRoleValid: false,
      }, () => this.validateAll());
      return;
    }

    this.setState({
      roleErrorMessage: null,
      isRoleValid: true,
    }, () => this.validateAll());
  };

  customExpirationDateValidation = formData => {
    if (!formData.checkedClients.value.length) {
      this.setState({
        expirationDateErrorMessage: 'Expiration date is required.',
        isExpirationDateValid: false,
      }, () => this.validateAll());
      return;
    }
    const clientsWithoutExpirationDateValues = formData.checkedClients.value.filter(checkedClient => (
      !formData.expirationDate.value.some(expirationDate => expirationDate.clientId === checkedClient.clientId)
    ));

    if (clientsWithoutExpirationDateValues.length) {
      const clientNames = clientsWithoutExpirationDateValues.map(({ name }) => `"${name}"`).join(', ');
      this.setState({
        expirationDateErrorMessage: `Expiration date is required. Clients ${clientNames} do not have an expiration date.`,
        isExpirationDateValid: false,
      }, () => this.validateAll());
      return;
    }

    this.setState({
      expirationDateErrorMessage: null,
      isExpirationDateValid: true,
    }, () => this.validateAll());
  };

  resetForm = () => {
    FormHelpers.resetForm({
      initialState: {
        ...getInitialState().formData,
      },
      setState: this.setState.bind(this),
      callback: () => {
        this.setState({
          shouldResetValues: true,
          isEditMode: false,
        }, () => {
          this.validateAll();
        });
      },
    });
  };

  saveAfterVerifyingEmail = () => {
    const { formData } = this.state;
    const { checkIfUserEmailIsUnderDomain } = this.props;

    if (isInternalEmailAddress(formData.email.value)) {
      this.setState({ isSaveDialogVisible: false });
      this.saveUserSettings();
    } else {
      checkIfUserEmailIsUnderDomain({ email: formData.email.value })
        .then(() => {
          const { isUserEmailUnderDomain } = this.props;
          if (!isUserEmailUnderDomain) {
            this.setState({ isSaveDialogVisible: false });
            return;
          }
          this.setState({ isSaveDialogVisible: true });
        });
    }
  };

  saveUserSettings = () => {
    const { setUserModified } = this.props;
    const { isEditMode } = this.state;

    this.setState({ isSaveDialogVisible: false });
    const saveStrategy = userSettingsData => this.props.saveUserSettings({
      userSettingsData,
      isEditMode,
      usersGridApi: this.props.usersGridApi,
    }, () => {
      this.props.showUserSettingsFormForView(userSettingsData.sso);
      if (setUserModified) {
        setUserModified(false);
      }
    });

    this.save(saveStrategy);
  };

  save = saveStrategy => {
    const {
      openForEdit,
      isSSOUnique,
      isEmailUnique,
      isUserEmailUnderDomain,
      isCheckingIfUsersEmailExists,
      isCheckingIfUserEmailIsUnderDomain,
      isCheckingIfUsersSSOExists,
    } = this.props;

    const { formData, isFormValid } = this.state;

    if (!isFormValid
      || !(isSSOUnique || openForEdit)
      || !isSsoAlphanumeric(formData.sso.value)
      || !isEmailUnique
      || !isUserEmailUnderDomain
      || isCheckingIfUsersEmailExists
      || isCheckingIfUserEmailIsUnderDomain
      || isCheckingIfUsersSSOExists
    ) {
      this.setState({
        triedToSubmitForm: true,
      });

      return;
    }

    const userSettingsData = Object.keys(formData).reduce((acc, key) => {
      acc[key] = formData[key].value;
      return acc;
    }, {});
    saveStrategy(userSettingsData);
  };

  hideSaveDialog = () => {
    this.setState({ isSaveDialogVisible: false });
  };

  changeProperty = event => {
    FormHelpers.changeProperty({
      event,
      setState: this.setState.bind(this),
      callback: () => {
        this.setUserModified(true);
      },
    });
  };

  setUserModified = nextIsUserModified => {
    const { isUserModified, setUserModified } = this.props;
    if (isUserModified !== nextIsUserModified) {
      setUserModified(nextIsUserModified);
    }
  };

  checkClient = (client, checked) => {
    this.userClientsGridApi.redrawRows();
    FormHelpers.selectMultiValueProperty({
      stateKey: 'checkedClients',
      value: client,
      valueCompareKey: 'clientId',
      isSelected: checked,
      setState: this.setState.bind(this),
      callback: () => {
        this.props.setUserModified(true);
        if (!checked) {
          this.removeDataAssociatedWithClient(client);
        }
      },
    });
  };

  changeExpirationDate = (client, value) => {
    this.userClientsGridApi.redrawRows();
    FormHelpers.selectMultiValueProperty({
      stateKey: 'expirationDate',
      value: {
        clientId: client.clientId || this.state.selectedClient.clientId,
        expirationDate: value,
      },
      valueCompareKey: 'clientId',
      isSelected: !!value,
      allowDuplicates: false,
      setState: this.setState.bind(this),
      callback: async () => {
        this.props.setUserModified(true);
      },
    });
  };

  removeDataAssociatedWithClient = client => {
    this.setState(({ formData }) => {
      const filterByClient = ({ clientId }) => clientId !== client.clientId;
      const newRoleValue = formData.role.value.filter(filterByClient);
      const newPermissionsValue = formData.permissions.value.filter(filterByClient);
      const newCheckedAssignmentsValue = formData.checkedAssignments.value.filter(filterByClient);

      return {
        formData: {
          ...formData,
          role: {
            value: newRoleValue,
            isValid: Boolean(newRoleValue.length),
          },
          permissions: {
            value: newPermissionsValue,
            isValid: true,
          },
          checkedAssignments: {
            value: newCheckedAssignmentsValue,
            isValid: true,
          },
        },
      };
    });
  };

  updateRoleAssignments = (roleAssignments, roleAssignmentsMaps, roleChangeDetails) => {
    this.setState(({ formData, ...prevState }) => ({
      ...prevState,
      formData: {
        ...formData,
        checkedAssignments: {
          ...formData.checkedAssignments,
          value: applyNewRoleAssignments(
            formData.checkedAssignments.value,
            roleAssignments,
            roleAssignmentsMaps,
            roleChangeDetails,
            prevState.selectedClient.clientId
          ),
        },
        permissions: {
          ...formData.permissions,
        },
      },
    }));
  };

  selectRole = role => {
    FormHelpers.selectMultiValueProperty({
      stateKey: 'role',
      value: {
        name: role?.value || null,
        roleId: role?.roleId || null,
        clientId: role?.clientId || this.state.selectedClient.clientId,
      },
      valueCompareKey: 'clientId',
      isSelected: !!role,
      allowDuplicates: false,
      setState: this.setState.bind(this),
      callback: async () => {
        const { fetchRoleAssignments, userToEdit } = this.props;
        const { formData, selectedClient, initialCheckedAssignments } = this.state;

        const permissionsForClient = userToEdit?.clients.find(
          ({ clientId }) => clientId === selectedClient.clientId)
          ?.userPermissions.map(({ privilegeCode }) => privilegeCode);

        const selectedRole = formData.role.value.find(
          ({ clientId }) => clientId === (selectedClient ? selectedClient.clientId : null)
        );
        const previousRole = userToEdit?.clients.find(({ clientId }) => clientId === selectedClient.clientId)
          ?.roleName;

        const { roleAssignments, roleAssignmentsMaps } = (selectedRole.name !== userRoles.USER)
          ? await fetchRoleAssignments({ roleId: role?.roleId })
          : { roleAssignmentsMaps: {}, roleAssignments: [] };

        const roleChangeDetails = {
          newRole: selectedRole.name,
          roleChanged: selectedRole.name === previousRole,
          initialCheckedAssignments,
        };

        this.updateRoleAssignments(roleAssignments, roleAssignmentsMaps, roleChangeDetails);

        const rolePrivileges = roleAssignmentsMaps?.USR?.privileges || [];

        const getUniquePermissions = (previousRole, selectedRole, permissionsForClient, rolePrivileges) => {
          if (previousRole === userRoles.USER && (selectedRole === userRoles.POWER_USER
              || selectedRole === userRoles.SUPER_USER)) {
            return [...new Set([...rolePrivileges])];
          } else if ((previousRole === userRoles.POWER_USER || previousRole === userRoles.SUPER_USER)
              && (selectedRole === userRoles.POWER_USER || selectedRole === userRoles.SUPER_USER)) {
            return [...new Set(rolePrivileges)];
          } else if ((previousRole === userRoles.USER) && selectedRole === userRoles.USER) {
            return [...new Set([...permissionsForClient])];
          }
          return [];
        };

        const uniqueNewPermissions = getUniquePermissions(previousRole, selectedRole.name,
          permissionsForClient, rolePrivileges);

        return this.changePermissions(uniqueNewPermissions);
      },
    });
    this.props.setUserModified(true);
  };


  changePermissions = permissions => {
    FormHelpers.selectMultiValueProperty({
      stateKey: 'permissions',
      value: {
        permissions,
        clientId: this.state.selectedClient.clientId,
      },
      valueCompareKey: 'clientId',
      isSelected: true,
      allowDuplicates: false,
      setState: this.setState.bind(this),
    });
  };


  checkAssignment = (assignmentCode, checked, assignmentType) => {
    const { setUserModified } = this.props;
    setUserModified(true);

    const roleAssignment = this.getCurrentClientRoleAssignment({
      assignmentType,
      assignmentCode,
    });

    FormHelpers.selectMultiValueProperty({
      stateKey: 'checkedAssignments',
      value: {
        clientId: this.state.selectedClient.clientId,
        assignmentCode,
        assignmentType,
        privileges: checked && roleAssignment?.privileges
          ? roleAssignment.privileges
          : [],
      },
      isSelected: checked,
      valueSelector: (element, incomingValue) => (
        element.assignmentCode === incomingValue.assignmentCode
        && element.assignmentType === incomingValue.assignmentType
        && element.clientId === incomingValue.clientId
      ),
      setState: this.setState.bind(this),
    });
  };

  checkAllAssignments = (values, checked, type) => {
    this.props.setUserModified(true);

    this.setState(({ formData, selectedClient }) => {
      if (checked) {
        return {
          formData: {
            ...formData,
            checkedAssignments: {
              ...formData.checkedAssignments,
              value: formData.checkedAssignments.value.concat(values.map(value => ({
                clientId: selectedClient.clientId,
                assignmentCode: value,
                assignmentType: type,
                privileges: [],
              }))),
            },
          },
        };
      }

      return {
        formData: {
          ...formData,
          checkedAssignments: {
            ...formData.checkedAssignments,
            value: formData.checkedAssignments.value.filter(
              ({ assignmentType, clientId }) => !(
                assignmentType === type && clientId === selectedClient.clientId
              )
            ),
          },
        },
      };
    });
  };

  getCurrentClientRoleAssignments = () => {
    const { formData, selectedClient } = this.state;
    const { rolesAssignmentsMap } = this.props;

    const isClientSelected = ({ clientId }) => clientId === (selectedClient ? selectedClient.clientId : null);

    const selectedRole = formData.role.value.find(isClientSelected);

    return rolesAssignmentsMap
      ?.[selectedRole?.roleId];
  };

  getCurrentClientRoleAssignment = assignment =>
    this.getCurrentClientRoleAssignments()?.[getAssignmentKey(assignment)];

  changeAssignmentsPermissions = ({ selectedAssignments, assignmentTypeCode, newValues, removedValues }) => {
    const {
      setUserModified,
    } = this.props;

    setUserModified(true);

    this.setState(({ formData, selectedClient }) => {
      const currentlySelectedClientId = selectedClient.clientId;

      const getNewValue = previousAssignments => {
        const newValue = previousAssignments.slice();

        selectedAssignments.forEach(selectedAssignmentCode => {
          const assignmentToUpdate = newValue.find(({ assignmentCode, assignmentType, clientId }) => (
            assignmentCode === selectedAssignmentCode
            && assignmentType === assignmentTypeCode
            && clientId === currentlySelectedClientId
          ));

          if (assignmentToUpdate) {
            assignmentToUpdate.privileges = assignmentToUpdate.privileges
              .filter(value => !removedValues.includes(value))
              .concat(newValues.filter(newValue => !assignmentToUpdate.privileges.includes(newValue)));
          } else {
            newValue.push({
              clientId: currentlySelectedClientId,
              assignmentCode: selectedAssignmentCode,
              assignmentType: assignmentTypeCode,
              privileges: newValues,
            });
          }
        });

        return newValue;
      };

      return {
        formData: {
          ...formData,
          checkedAssignments: {
            ...formData.checkedAssignments,
            value: getNewValue(formData.checkedAssignments.value),
          },
        },
      };
    });
  };

  changeTab = (event, activeTab) => this.setState({ activeTab });

  changeCompanyAssignmentsPill = (pillType, callback) => {
    this.setState({
      activeCompanyAssignmentsPill: pillType,
    }, () => {
      if (callback) {
        setTimeout(callback, 0);
      }
    });
  };

  hideForm = () => {
    if (this.props.openForEdit) {
      this.props.hideUserSettingsFormForEdit();
      this.props.deselectUser();
      return;
    }
    this.props.hideUserSettingsFormForAdd();
    this.props.deselectUser();
  };

  disableReadOnlyMode = () => {
    this.props.disableReadOnlyMode();
  };

  showSetLastClientIdModal = () => {
    this.props.showSetLastClientIdModal();
  };

  render() {
    const {
      activeTab,
      formData,
      triedToSubmitForm,
      shouldResetValues,
      isEditMode,
      activeCompanyAssignmentsPill,
      selectedClient,
      roleErrorMessage,
      isRoleValid,
      isExpirationDateValid,
      expirationDateErrorMessage,
      isSaveDialogVisible,
    } = this.state;

    const {
      availablePermissions,
      userToEdit,
      isSavingInProgress,
      userSettingsFormData,
      readOnly,
      userRole,
      rolesOptions,
      doesNotHavePermissionToEdit,
      loggedInUser,
      loggedInUserAssignments,
      canEditClients,
      defaultClient,
      clientsFormData,
      canModify,
      isSSOUnique,
      isEmailUnique,
      isUserEmailUnderDomain,
      isCheckingIfUsersEmailExists,
      isCheckingIfUserEmailIsUnderDomain,
      isCheckingIfUsersSSOExists,
      openForEdit,
      canHaveLastClientSet,
      isLoadingRolesAssignmentsMap,
      isFetchingInitialDataForUserSettingsForm,
      canEditExpirationDate,
    } = this.props;

    const isClientSelected = ({ clientId }) => clientId === (selectedClient ? selectedClient.clientId : null);

    const editedUserCurrentClientRole = loggedInUser && userToEdit
      ? userToEdit.clients.find(client => isClientSelected(client))?.roleName
      : null;
    const loggedInUserCurrentClientRole = loggedInUser
      ? loggedInUser.clients.find(client => isClientSelected(client))?.roleName
      : null;

    const isDisabledExpirationDate = formData?.sso.value === this.props.loggedInUser?.sso || !canEditExpirationDate;

    const userCanEditRole = canUserEditRole(loggedInUserCurrentClientRole, editedUserCurrentClientRole);

    const filteredRolesOptions = rolesOptions.map(({ clientId, roles }) => {
      if (!selectedClient || selectedClient?.clientId !== clientId) {
        return { clientId, roles };
      }
      return {
        clientId,
        roles: roles.filter(({ roleName }) => canUserEditRole(loggedInUserCurrentClientRole, roleName)),
      };
    });

    const readOnlyMode = readOnly || !userCanEditRole;

    const isLoadingDataToEdit = (isEditMode && !userToEdit) || isLoadingRolesAssignmentsMap;

    const isSelectedClientChecked = formData.checkedClients.value
      .some(checkedClient => checkedClient.clientId === selectedClient.clientId);

    const setDefaultExpDate = setDefaultExpirationDate(formData.expirationDate?.value, selectedClient?.clientId);
    const expirationDateDefault = formData.checkedClients.value.length
      && isSelectedClientChecked && setDefaultExpDate
      ? formData.expirationDate.value.push({
        clientId: selectedClient.clientId,
        expirationDate: moment().add(1, 'year').format('YYYY-MM-DD'),
      }) : [];

    const selectedRole = formData.role.value.find(isClientSelected);
    const selectedRoleFormValue = selectedRole ? selectedRole.name : null;
    let loggedInUserPermissions = [];

    if (loggedInUser) {
      const loggedInUserClient = loggedInUser?.clients?.filter(item => item.clientId === selectedClient?.clientId);
      if (loggedInUserClient.length > 0) {
        loggedInUserPermissions = loggedInUserClient[0].userPermissions?.map(item => item.privilegeCode);
      }
    }

    const rolePrivileges = this.getCurrentClientRoleAssignment(userAssignment)?.privilegesMap;
    const selectedPermissions = formData?.permissions?.value.find(isClientSelected);
    const selectedPermissionsFormValue = selectedPermissions ? selectedPermissions.permissions : [];
    const defaultClientRole = formData.role.value.find(role => role.clientId === defaultClient.clientId);

    const selectedAssignmentsFormValue = formData.checkedAssignments.value.filter(isClientSelected);

    const { canEdit: canEditClient } = canEditClients.find(isClientSelected) || { canEdit: false };

    const shouldShowAllCheckbox = loggedInUserAssignments
      .filter(isClientSelected)
      .filter(filterAssignmentsByType('AL')).length > 0
      || userRole === userRoles.SUPER_USER
      || userRole === userRoles.POWER_USER;

    const userSettingsFormDataFilteredByClient = filteredSettingsFormDataSelector(
      selectedClient ? selectedClient.clientId : null,
      isSelectedClientChecked,
      userSettingsFormData
    );

    const userSettingsFormDataFilteredByPrivileges = readOnlyMode
      || userRole === userRoles.SUPER_USER
      || userRole === userRoles.POWER_USER
      ? userSettingsFormDataFilteredByClient
      : filterByPrivileges(userSettingsFormDataFilteredByClient, loggedInUserAssignments, isClientSelected);

    const canEditPrivilegesAndPermissions = userToEdit
      ? isAllowedToEditPrivilegesAndPermissions({
        role: selectedRoleFormValue,
        selectedClient,
        rolesOptions: filteredRolesOptions,
      })
      : true;

    const isEmailChanged = isEditMode
      ? userToEdit && userToEdit.email !== formData.email.value
      : true;

    let emailError = null;
    if (triedToSubmitForm && !isCheckingIfUsersEmailExists && !isEmailUnique && isEmailChanged) {
      emailError = DUPLICATED_EMAIL_ERROR;
    } else if (!isUserEmailUnderDomain && formData.email.value !== '') {
      emailError = USER_EMAIL_DOMAIN_ERROR;
    }

    let ssoError = null;
    if (formData.sso.value !== '' && !isSsoAlphanumeric(formData.sso.value)) {
      ssoError = SSO_ALPHANUMERIC_ERROR;
    } else if (triedToSubmitForm && !isCheckingIfUsersSSOExists && !isSSOUnique && !openForEdit) {
      ssoError = DUPLICATED_SSO_ERROR;
    }

    const isSaveButtonDisabled = isSavingInProgress
      || isLoadingDataToEdit
      || isCheckingIfUsersEmailExists
      || isCheckingIfUserEmailIsUnderDomain
      || isCheckingIfUsersSSOExists
      || isFetchingInitialDataForUserSettingsForm;

    const clients = clientsFormData.map(c => {
      const existingClient = userToEdit?.clients?.find(client => client.clientId === c.clientId);
      return existingClient || { ...c, ...DEFAULT_CLIENT };
    });

    return (
      <div className="add-edit-user-settings-form page-content-container">
        <form>
          <Tabs
            defaultValue={TabTypes.SETTINGS}
            value={activeTab}
            onChange={this.changeTab}
            className="a-tab-bar-margin-10"
            size="md"
          >
            <Tab value={TabTypes.SETTINGS} label="Settings">
              {isRoleValid || !triedToSubmitForm
                ? null
                : (
                  <div className="row">
                    <div className="col mb-2 text-danger">
                      {roleErrorMessage || 'Role is required.'}
                    </div>
                  </div>
                )
              }
              {isExpirationDateValid || !triedToSubmitForm
                ? null
                : (
                  <div className="row">
                    <div className="col mb-2 text-danger">
                      {expirationDateErrorMessage || 'Expiration date is required.'}
                    </div>
                  </div>)
              }
              <div className="row">

                <div className="col-sm-12 col-lg-6">
                  <FormControl
                    onChange={this.changeProperty}
                    name="email"
                    label="Email"
                    validate={this.validate}
                    validationSubscription={this.validationSubscription}
                    value={formData.email.value}
                    required
                    showErrorByForce={triedToSubmitForm}
                    shouldReset={shouldResetValues}
                    horizontal
                    globalFormError={emailError}
                  >
                    <Input
                      disabled={isLoadingDataToEdit || readOnlyMode}
                      type="text"
                      suffix={isCheckingIfUsersEmailExists
                        && isCheckingIfUserEmailIsUnderDomain ? <Spinner small /> : null}
                    />
                  </FormControl>
                  <FormControl
                    onChange={this.changeProperty}
                    name="firstName"
                    label="First Name"
                    validate={this.validate}
                    validationSubscription={this.validationSubscription}
                    value={formData.firstName.value}
                    required
                    showErrorByForce={triedToSubmitForm}
                    shouldReset={shouldResetValues}
                    horizontal
                  >
                    <Input
                      disabled={isLoadingDataToEdit || readOnlyMode}
                      type="text"
                    />
                  </FormControl>
                  <FormControl
                    onChange={this.changeProperty}
                    name="lastName"
                    label="Last Name"
                    validate={this.validate}
                    validationSubscription={this.validationSubscription}
                    value={formData.lastName.value}
                    required
                    showErrorByForce={triedToSubmitForm}
                    shouldReset={shouldResetValues}
                    horizontal
                  >
                    <Input
                      disabled={isLoadingDataToEdit || readOnlyMode}
                      type="text"
                    />
                  </FormControl>
                  <FormControl
                    onChange={this.changeProperty}
                    name="sso"
                    label="SSO"
                    validate={this.validate}
                    validationSubscription={this.validationSubscription}
                    value={formData.sso.value}
                    required
                    showErrorByForce={triedToSubmitForm}
                    shouldReset={shouldResetValues}
                    horizontal
                    globalFormError={ssoError}
                  >
                    <Input
                      disabled={isEditMode || isLoadingDataToEdit || readOnlyMode}
                      type="text"
                      maxLength={9}
                      suffix={isCheckingIfUsersSSOExists ? <Spinner small /> : null}
                    />
                  </FormControl>
                  <FormControl
                    onChange={this.changeProperty}
                    name="activeFlag"
                    label="Active"
                    validate={this.validate}
                    validationSubscription={this.validationSubscription}
                    showErrorByForce={triedToSubmitForm}
                    shouldReset={shouldResetValues}
                    horizontal
                  >
                    <input
                      checked={formData.activeFlag.value}
                      disabled={isLoadingDataToEdit || readOnlyMode}
                      type="checkbox"
                    />
                  </FormControl>
                  {isEditMode ? (
                    <Button
                      type="button"
                      kind="secondary"
                      disabled={isSavingInProgress || isLoadingDataToEdit || readOnlyMode || !canHaveLastClientSet}
                      onClick={this.showSetLastClientIdModal}
                    >
                      Set last client
                    </Button>
                  ) : null}
                </div>
                <div className="col-sm-12 col-lg-6">
                  <FormControl
                    name="checkedClients"
                    label="Clients"
                    hideLabel
                    validate={this.validate}
                    validationSubscription={this.validationSubscription}
                    showErrorByForce={triedToSubmitForm}
                    shouldReset={shouldResetValues}
                    value={formData.checkedClients.value}
                    required
                    hideAsterisk
                    customErrorMessage="At least one Client is required"
                  >
                    <UserClientsGrid
                      loggedInUser={this.props.loggedInUser || {}}
                      rolesOptions={filteredRolesOptions}
                      clients={clients}
                      checkClient={this.checkClient}
                      selectRole={this.selectRole}
                      readOnly={readOnlyMode}
                      rolesValues={formData.role.value}
                      onGridReady={this.onUserClientsGridReady}
                      expirationDateValues={formData.expirationDate.value || expirationDateDefault}
                      changeExpirationDate={this.changeExpirationDate}
                      isLoading={isSavingInProgress || isFetchingInitialDataForUserSettingsForm}
                      disabled={isDisabledExpirationDate}
                    />
                  </FormControl>
                </div>
              </div>
            </Tab>

            <Tab value={TabTypes.USER_PRIVILEGES} label="User Privileges">
              <UserPrivilegesTab
                availablePermissions={availablePermissions}
                roleBasedPermissions={rolePrivileges}
                loggedInUserPermissions={loggedInUserPermissions}
                changePermissions={this.changePermissions}
                defaultClientRole={defaultClientRole}
                defaultClient={defaultClient}
                isLoadingDataToEdit={isLoadingDataToEdit}
                isRoleValid={isRoleValid}
                readOnly={readOnlyMode
                  || !canEditPrivilegesAndPermissions
                  || !canEditClient
                  || selectedRole?.name === userRoles.SUPER_USER
                }
                roleErrorMessage={roleErrorMessage}
                selectedClient={selectedClient}
                selectedPermissionsFormValue={selectedPermissionsFormValue}
                selectedRole={selectedRole}
                shouldResetValues={shouldResetValues}
                triedToSubmitForm={triedToSubmitForm}
                checkedAssignments={selectedAssignmentsFormValue}
                changeAssignmentsPermissions={this.changeAssignmentsPermissions}
                availableAssignmentPermissions={userSettingsFormData.availableAssignmentPermissions}
              />
            </Tab>

            <Tab value={TabTypes.COMPANY_ASSIGNMENTS_AND_PRIVILEGES} label="Company Assignments and Privileges">
              <CompanyAssignments
                defaultClientRole={defaultClientRole}
                userSettingsFormData={userSettingsFormDataFilteredByPrivileges}
                checkAssignment={this.checkAssignment}
                checkAllAssignments={this.checkAllAssignments}
                loggedInUserCheckedAssignments={loggedInUserAssignments}
                checkedAssignments={selectedAssignmentsFormValue}
                changeCompanyAssignmentsPill={this.changeCompanyAssignmentsPill}
                activeCompanyAssignmentsPill={activeCompanyAssignmentsPill}
                changeAssignmentsPermissions={this.changeAssignmentsPermissions}
                isVisible={activeTab === TabTypes.COMPANY_ASSIGNMENTS_AND_PRIVILEGES}
                readOnly={isLoadingDataToEdit
                  || readOnlyMode
                  || !selectedClient
                  || !canEditPrivilegesAndPermissions
                  || !canEditClient
                  || selectedRole?.name === userRoles.SUPER_USER
                }
                userRole={userRole}
                currentRoleAssignmentsMap={this.getCurrentClientRoleAssignments()}
                showAllCheckbox={shouldShowAllCheckbox}
                elementToRenderBeforePills={
                  <div className="row">
                    <div className="col-12 col-md-2 mb-4">
                      <div className="user-client-label">
                        {`Client: ${defaultClient.name || 'All'}`}
                      </div>
                    </div>
                  </div>
                }
                selectedClient={selectedClient}
                isSelectedClientChecked={isSelectedClientChecked}
                availablePermissions={availablePermissions}
                selectedRole={selectedRole}
                selectedPermissionsFormValue={selectedPermissionsFormValue}
              />
            </Tab>
          </Tabs>
        </form>

        <div className="d-flex flex-row-reverse mt-2">
          {readOnlyMode
            ? null
            : (
              <Button
                type="button"
                kind="primary"
                disabled={isSaveButtonDisabled}
                onClick={this.saveAfterVerifyingEmail}
              >
                Save
              </Button>
            )
          }
          {!readOnly || !canModify || doesNotHavePermissionToEdit
            ? null
            : (
              <Button
                type="button"
                kind="primary"
                disabled={isSaveButtonDisabled || !userCanEditRole}
                onClick={this.disableReadOnlyMode}
              >
                Edit User Settings
              </Button>
            )
          }
          <Button
            type="button"
            kind="secondary"
            className="a-mx-10"
            onClick={this.hideForm}
            disabled={isSavingInProgress}
          >
            Cancel
          </Button>
        </div>
        <WarningDialog
          onConfirm={this.saveUserSettings}
          onCancel={this.hideSaveDialog}
          message={<ConfirmationDialogContent />}
          title="Warning!"
          dismissText="Cancel"
          submitText="Save"
          isVisible={isSaveDialogVisible}
        />
        <SetLastClientModal />
        <ReactTooltip />
      </div >
    );
  }
}

function mapStateToProps(state) {
  const { user, users, shared } = state;
  const userToEdit = users.userToEdit;
  const loggedInUser = users.loggedInUser;

  const loggedInUserSso = user.user.sso;
  const canEditHimself = user.user.permissions.commands.canEditHimself;

  return {
    loggedInUserSso,
    loggedInUser,
    loggedInUserAssignments: normalizedLoggedInUserAssignmentsSelector(state),
    userToEdit,
    userRole: user.user.role,
    readOnly: users.readOnly,
    isSavingInProgress: users.isSavingInProgress,
    currentPeriod: shared.currentPeriod,
    userSettingsFormData: settingsFormDataSelector(state),
    rolesOptions: rolesOptionsSelector(state),
    availablePermissions: availablePermissionsForRoleSelector(state),
    doesNotHavePermissionToEdit: userToEdit && (
      (userToEdit.sso === loggedInUserSso && !canEditHimself)
      || !isAllowedToEditUsersSelector(state)
    ),
    rolesAssignmentsMap: rolesAssignmentsMapsSelector(state),
    isLoadingRolesAssignmentsMap: isLoadingRolesAssignmentsMapSelector(state),
    canEditClients: canEditClientsSelector(state),
    defaultClient: currentUserClientSelector(state),
    clientsFormData: clientsFormDataSelector(state),
    isEmailUnique: isEmailUniqueSelector(state),
    isUserEmailUnderDomain: isUserEmailUnderDomainSelector(state),
    isSSOUnique: isSSOUniqueSelector(state),
    isCheckingIfUsersEmailExists: isCheckingIfUsersEmailExistsSelector(state),
    isCheckingIfUserEmailIsUnderDomain: isCheckingIfUserEmailIsUnderDomainSelector(state),
    isCheckingIfUsersSSOExists: isCheckingIfUsersSSOExistsSelector(state),
    canHaveLastClientSet: canHaveLastClientSetSelector(state),
    isFetchingInitialDataForUserSettingsForm: isFetchingInitialDataForUserSettingsFormSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchRoleAssignments,
    fetchInitialDataForUserSettingsForm,
    fetchLoggedInUserSettings,
    hideUserSettingsFormForAdd,
    hideUserSettingsFormForEdit,
    saveUserSettings,
    disableReadOnlyMode,
    checkIfUserSSOExists,
    checkIfUserEmailExists,
    checkIfUserEmailIsUnderDomain,
    showSetLastClientIdModal,
  }
)(AddEditUserSettingsForm);
